// Экран «Этажи» (структура проекта) — второй экран нового фронта (п.20, 2026-09-07).
// Колонка на корпус, этажи сплошным столбиком сверху вниз (общие уровни убраны
// 2026-09-12: дробные номера кровель плодили пустые строки у соседних корпусов);
// корпуса выровнены по земле (владелец 2026-09-12): 1-й этаж у всех на одной строке,
// высокие корпуса растут вверх, подземные этажи добирают пустыми местами снизу;
// при открытии доска прокручена к земле,
// цвет по типу этажа, приоритет в ячейке,
// карточка этажа, карточка корпуса (переименовать / этажность / удалить),
// «+ этажи», «+ корпус», мультивыбор (Ctrl/Shift или галочка) и пакетная правка.
// 2026-09-09 (владелец): показываются только корпуса, выбранные в панели фильтров
// (сама панель на этом экране оставлена только с корпусами), а что видно в ячейке
// этажа — выбирается кнопками в шапке (взаимоисключающими, владелец 2026-09-09):
// «Приоритет» (поле приоритета) или «Пуск тепла» (плановая и фактическая даты
// пуска и отметка «ТК закрыт»); повторный клик по нажатой кнопке возвращает
// простой вид — только этаж и его тип. Плановую дату держит расчёт работ теплового контура, факт и
// отметка — отчётные, на сроки не влияют. Галочки «мультивыбор» нет: несколько
// этажей выделяются Ctrl/Shift-кликом. Захватка из окон убрана 2026-09-12 (владелец:
// «добавим в следующих итерациях») — поле в базе и API живёт, значения не трогаем.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { call, p, type Data } from '../api';
import { Pop, at, type Anchor } from '../Pop';
import type { ShellCtx } from '../shell';
import { TIPY_ETAZHA, fmt, parseDate, tipColor } from '../util';

type Structure = Data<'/api/projects/{pid}/structure', 'get'>;
type Floor = Structure[string][number];

type PopState =
  | { kind: 'floor'; korpus: string; f: Floor; at: Anchor }
  | { kind: 'building'; korpus: string; at: Anchor }
  | { kind: 'dopFloor'; korpus: string; at: Anchor }
  | { kind: 'newBuilding'; at: Anchor }
  | { kind: 'bulk'; at: Anchor }
  | { kind: 'prioGruppy'; at: Anchor }
  | null;

/** этажи корпуса сверху вниз: больший номер выше, безномерные — на самый верх */
/** «8 работ», «1 работа», «3 работы» (#29) */
function rabot(n: number): string {
  const m10 = n % 10, m100 = n % 100;
  const slovo = m100 >= 11 && m100 <= 14 ? 'работ' : m10 === 1 ? 'работа' : m10 >= 2 && m10 <= 4 ? 'работы' : 'работ';
  return n + ' ' + slovo;
}

/** этаж заведён вручную (доп. этаж): метка не совпадает с номером или номера нет —
    только у таких есть смысл менять «Положение» (#29) */
function ruchnoyEtazh(f: Floor): boolean {
  return f.num == null || String(f.num) !== String(f.etazh).trim();
}

/** Подземные этажи (паркинг, подвалы), 2026-09-09: сколько минусовых этажей
    завести. Принимаем и «3», и «-3» — оба значат этажи −1, −2, −3. */
const TIP_PODZEM = 'Ниже 0,000';
function podzemnyh(v: string): number | null {
  const t = v.trim();
  if (t === '') return 0;
  const n = Math.abs(parseInt(t, 10));
  return Number.isInteger(n) && n >= 1 && n <= 300 ? n : null;
}

/** что показывать в ячейке этажа: обычный вид или пуск тепла. Кнопки «Приоритет»
    больше нет (замечание владельца #22, 2026-09-12): приоритет вводится в окне этажа
    и показывается в ячейке сам, когда задан */
type Rezhim = '' | 'pusk';

const REZHIMY: { kod: Rezhim; imya: string; podskazka: string }[] = [
  { kod: 'pusk', imya: 'Пуск тепла',
    podskazka: 'Показать в каждом этаже даты закрытия теплового контура и пуска тепла с отметками «случилось»' },
];

function loadRezhim(): Rezhim {
  try {
    const v = localStorage.getItem('plot_struct_rezhim');
    if (v !== null) return v === 'pusk' ? v : '';   // прежний режим «pri» → обычный вид
    // разовый перенос памяти прежних галочек (2026-09-09), чтобы вид не
    // «сбросился» на глазах; старые ключи после переноса убираем — иначе они
    // каждый раз перебивали выключенный режим
    const staryj: Rezhim = localStorage.getItem('plot_struct_pusk') === '1' ? 'pusk' : '';
    localStorage.removeItem('plot_struct_pusk');
    localStorage.removeItem('plot_struct_pri');
    localStorage.setItem('plot_struct_rezhim', staryj);
    return staryj;
  } catch { /* приватный режим */ }
  return '';
}
function saveRezhim(r: Rezhim) {
  try { localStorage.setItem('plot_struct_rezhim', r); } catch { /* приватный режим */ }
}

export function Struct({ ctx }: { ctx: ShellCtx }) {
  const [struct, setStruct] = useState<Structure>({});
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [selLast, setSelLast] = useState<{ k: string; id: number } | null>(null);
  const [rezhim, setRezhim] = useState<Rezhim>(loadRezhim);
  const puskMode = rezhim === 'pusk';
  const pereklyuchi = (r: Rezhim) => { const next = rezhim === r ? '' : r; setRezhim(next); saveRezhim(next); };
  // приоритет: «Все работы» (общий приоритет этажа) или отдельная группа работ
  // (#66, владелец 2026-09-18: порядок этажей у кладки и у отделки разный)
  const [gruppa, setGruppa] = useState<string>(() => {
    try { return localStorage.getItem('plot_struct_prio_gruppa') || ''; } catch { return ''; }
  });
  const [gruppy, setGruppy] = useState<string[]>([]);
  const vybratGruppu = (g: string) => {
    setGruppa(g);
    try { localStorage.setItem('plot_struct_prio_gruppa', g); } catch { /* приватный режим */ }
  };
  const [zagruzheno, setZagruzheno] = useState(false);
  const [msg, setMsg] = useState('');
  const [pop, setPop] = useState<PopState>(null);
  const can = ctx.canPlan;
  const doska = useRef<HTMLDivElement>(null);
  // фильтр по типам этажей (владелец, #24 2026-09-12): пусто = показывать все
  const [tipy, setTipyState] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('plot_struct_tipy') || '[]') as string[]; } catch { return []; }
  });
  const setTipy = (v: string[]) => {
    setTipyState(v);
    try { localStorage.setItem('plot_struct_tipy', JSON.stringify(v)); } catch { /* приватный режим */ }
  };
  const pereklTip = (t: string) => setTipy(tipy.includes(t) ? tipy.filter(x => x !== t) : [...tipy, t]);
  const tipPodhodit = (f: Floor) => !tipy.length || tipy.includes(f.tip || '');

  const load = useCallback(async () => {
    const s = await call<Structure>('get', p('/api/projects/{pid}/structure', { pid: ctx.pid }));
    if (!s.ok) { setMsg(s.error.message); return; }
    setStruct(s.data); setZagruzheno(true);
    const alive = new Set<number>();
    Object.values(s.data).forEach(fs => fs.forEach(f => alive.add(f.id)));
    setSel(prev => new Set([...prev].filter(id => alive.has(id))));
    const sp = await call<{ kod: string; znachenie: string }[]>('get', p('/api/projects/{pid}/spiski', { pid: ctx.pid }));
    if (sp.ok) setGruppy(sp.data.filter(x => x.kod === 'gruppa').map(x => x.znachenie).filter(Boolean));
  }, [ctx.pid]);
  useEffect(() => { void load(); }, [load]);

  const fail = (m: string) => { setMsg(m); ctx.onError?.(m); };
  const changed = (korpusChanged = false) => { void load(); if (korpusChanged) ctx.onChanged?.(); };

  // корпуса, выбранные в панели фильтров (владелец 2026-09-09); пусто в фильтре = все
  const korpusa = useMemo(
    () => Object.keys(struct).filter(k => (ctx.matchesKorpus ? ctx.matchesKorpus(k) : true))
      .sort((a, b) => a.localeCompare(b, 'ru', { numeric: true })),   // К1, К2 … К10 (2026-09-12)
    [struct, ctx]);


  // общие уровни (владелец, #28 2026-09-13; отменяет «сплошные столбики» от 12.09):
  // этажи с одинаковым номером у разных корпусов стоят в одной строке, у корпуса без
  // такого номера — пустое место. Без номера — своя строка сверху по метке.
  // Земля выравнивается сама: у всех корпусов 1-й этаж на одном уровне
  const urovni = useMemo(() => {
    const nums = new Set<number>(), bez = new Set<string>();
    korpusa.forEach(k => (struct[k] || []).filter(tipPodhodit).forEach(f => {
      if (f.num != null) nums.add(f.num); else bez.add(String(f.etazh));
    }));
    const verh = [...bez].sort((a, b) => b.localeCompare(a, 'ru', { numeric: true })).map(x => 'l:' + x);
    const chisla = [...nums].sort((a, b) => b - a).map(n => 'n:' + n);
    return [...verh, ...chisla];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [struct, korpusa, tipy]);
  const klyuch = (f: Floor) => (f.num != null ? 'n:' + f.num : 'l:' + String(f.etazh));
  // при открытии и смене режима — прокрутить к земле: там 1-й этаж всех корпусов
  useEffect(() => {
    const el = doska.current;
    if (el && zagruzheno) el.scrollTop = el.scrollHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zagruzheno, rezhim, korpusa.length]);

  async function patchFloor(f: Floor, patch: Record<string, unknown>) {
    const r = await call('patch', p('/api/floors/{fid}', { fid: f.id }), patch);
    if (!r.ok) { fail(r.error.message); changed(); return; }
    setMsg(''); changed();
  }
  const setPrior = (f: Floor, v: string) => (gruppa
    ? setPriorGruppy([f.id], v)
    : patchFloor(f, { avto_prioritet: v.trim() || null }));

  /** приоритет для выбранной группы работ: пусто снимает исключение — этаж снова
   *  идёт по общему приоритету (#66) */
  async function setPriorGruppy(ids: number[], v: string) {
    const t = v.trim();
    const r = await call<{ etazhej: number }>('put', p('/api/projects/{pid}/floors/prioritety', { pid: ctx.pid }),
      { ids, gruppa, prioritet: t === '' ? null : t });
    if (!r.ok) { fail(r.error.message); changed(); return; }
    setMsg(t === '' ? 'Приоритет группы снят: этажей ' + r.data.etazhej + '. Нажмите «Актуализировать».'
      : 'Приоритет группы «' + gruppa + '» задан: этажей ' + r.data.etazhej + '. Нажмите «Актуализировать».');
    changed();
  }
  function cellClick(k: string, f: Floor, e: React.MouseEvent) {
    if ((e.target as HTMLElement).closest('input.sb-pri, .sb-pusk')) return;
    if (!can) return;
    const multi = e.ctrlKey || e.metaKey || e.shiftKey;
    if (!multi) { setPop({ kind: 'floor', korpus: k, f, at: at(e) }); return; }
    const next = new Set(sel);
    if (e.shiftKey && selLast && selLast.k === k) {
      const arr = struct[k] || [];
      const i1 = arr.findIndex(x => x.id === selLast.id), i2 = arr.findIndex(x => x.id === f.id);
      if (i1 >= 0 && i2 >= 0) for (let j = Math.min(i1, i2); j <= Math.max(i1, i2); j++) next.add(arr[j].id);
    } else if (next.has(f.id)) next.delete(f.id); else next.add(f.id);
    setSel(next); setSelLast({ k, id: f.id });
  }

  return (
    <div>
      <div className="bar actbar" title="Клик по этажу — свойства; Ctrl или Shift при клике — выделить несколько; по названию корпуса — переименовать, этажность, удалить; «+ корпус» вверху — новый корпус">
        {sel.size > 0 && <button onClick={e => setPop({ kind: 'bulk', at: at(e) })}>Свойства выбранных ({sel.size})</button>}
        {sel.size > 0 && <button className="sec" onClick={() => { setSel(new Set()); setSelLast(null); }}>Снять выделение</button>}
        <span className="pv-stolbcy">
          {REZHIMY.map(r => (
            <button key={r.kod} className={'sec pv-btn' + (rezhim === r.kod ? ' on' : '')}
              title={r.podskazka + '. Клик по нажатой — вернуть простой вид'}
              onClick={() => pereklyuchi(r.kod)}>{r.imya}</button>
          ))}
        </span>
        {/* приоритет: общий или для группы работ (#66, 2026-09-18) */}
        <span className="pv-stolbcy" title="Чей приоритет показывать и править в плитках этажей: общий (действует на все работы) или отдельной группы работ — у неё он сильнее общего">
          <span className="pw-muted">приоритет:</span>
          <select value={gruppa} onChange={e => vybratGruppu(e.target.value)} style={{ maxWidth: 190 }}>
            <option value="">все работы</option>
            {gruppy.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          {gruppa && sel.size > 0 && can && (
            <button className="sec" onClick={e => setPop({ kind: 'prioGruppy', at: at(e) })}>
              Приоритет группы для выбранных ({sel.size})</button>)}
        </span>
        {/* фильтр по типам этажей (#24): чипы как в панели фильтров, пусто = все */}
        <span className="pv-stolbcy" title="Показать только этажи выбранных типов">
          <a className={'ktag' + (tipy.length ? '' : ' on')} onClick={() => setTipy([])}>все</a>
          {TIPY_ETAZHA.map(t => (
            <a key={t} className={'ktag' + (tipy.includes(t) ? ' on' : '')} onClick={() => pereklTip(t)}>{t}</a>
          ))}
        </span>
        {can && <button className="sec" title="Добавить корпус" onClick={e => setPop({ kind: 'newBuilding', at: at(e) })}>+ корпус</button>}
        <span className="grow" />
        <span className="pw-muted">{msg}</span>
      </div>
      <div className={'sb-wrap' + (puskMode ? ' pusk' : '')} ref={doska}
        style={{ display: 'flex', gap: 14, alignItems: 'flex-start', overflow: 'auto', maxHeight: '78vh', padding: '6px 2px' }}>
        {korpusa.map(k => {
          const rows: JSX.Element[] = [
            // название корпуса — кнопка (владелец 2026-09-12): этажность, подземные,
            // переименование, удаление; отдельной кнопки «+ этажи» больше нет
            <div key="head" className={'sb-head' + (can ? ' knopka' : '')}
              title={can ? 'Настроить корпус: этажность, подземные этажи, название, удаление' : k}
              onClick={e => { if (can) setPop({ kind: 'building', korpus: k, at: at(e) }); }}>
              <span>{k}</span>{can && <span className="sb-head-ed">настроить ▾</span>}</div>,
          ];
          const poUrovnyu = new Map<string, Floor>();
          (struct[k] || []).filter(tipPodhodit).forEach(f => { if (!poUrovnyu.has(klyuch(f))) poUrovnyu.set(klyuch(f), f); });
          urovni.forEach(u => {
            const f = poUrovnyu.get(u);
            if (!f) { rows.push(<div key={'v' + u} className="sb-void" />); return; }
            const cs = tipColor(f.tip);
            rows.push(
              <div key={f.id} className={'sb-cell' + (sel.has(f.id) ? ' sel' : '') + (f.vne_rascheta ? ' vne' : '')}
                style={cs ? { background: cs.bg, borderLeft: '4px solid ' + cs.bd } : undefined}
                title={(f.vne_rascheta ? 'ЭТАЖ ВНЕ РАСЧЁТА: все его задачи исключены из модели. ' : '')
                  + 'клик — свойства этажа; Ctrl/Shift — выделение'} onClick={e => cellClick(k, f, e)}>
                <span className="sb-lbl">{f.etazh}</span>
                {/* приоритет вводится прямо в плитке, поле видно всегда (владелец, #22/#25 2026-09-12) */}
                {!puskMode && <PriInput f={f} can={can} gruppa={gruppa} onSave={v => setPrior(f, v)} />}
                {/* справа: тип этажа, под ним число работ (#29); в режиме пуска тепла
                    вместо них события ТК. Тип «Исключение» = вне расчёта (#24), красный от tipColor */}
                {!puskMode && (
                  <span className="sb-r">
                    <span className="sb-tip">{f.tip || ''}</span>
                    {f.zadach > 0 && <span className="sb-n" title={'задач на этаже: ' + f.zadach}>{rabot(f.zadach)}</span>}
                  </span>)}
                {puskMode && <PuskPolya f={f} can={can} onFail={fail}
                  onSave={patch => patchFloor(f, patch)} />}
              </div>);
          });
          return (
            <div className="sb-col" key={k}>
              {rows}
              {can && <div className="sb-addf" title="кровля, технический этаж между жилыми: метка, тип и место — «над этажом 1»"
                onClick={e => setPop({ kind: 'dopFloor', korpus: k, at: at(e) })}>+ доп. этаж</div>}
            </div>
          );
        })}
        {zagruzheno && !korpusa.length && <div className="pw-muted" style={{ padding: '10px 2px' }}>
          {Object.keys(struct).length
            ? 'Выбранных корпусов в проекте нет — поменяйте выбор корпусов вверху.'
            : 'В проекте пока нет корпусов — добавьте плюсиком справа.'}</div>}
      </div>
      <div className="pw-muted" style={{ margin: '8px 0' }}>
        Приоритет — прямо в плитке: больше — раньше по всем работам, меньше нуля — в конец очереди;
        выбор группы работ в шапке переключает поле на приоритет этой группы (он сильнее общего).
        Тип «Исключение» — этаж вне расчёта.{' '}
        Название корпуса — кнопка: этажность, подземные этажи, переименование, удаление. Доп. этажи (кровля, технический) стоят по номеру: «над этажом 1» = 1,5. Этаж удаляется в его окне, если на нём нет задач.</div>

      {pop?.kind === 'floor' && <FloorPop ctx={ctx} pop={pop} floors={struct[pop.korpus] || []} onClose={() => setPop(null)}
        onDone={m => { setMsg(m); setPop(null); changed(); }} onFail={fail} />}
      {pop?.kind === 'building' && <BuildingPop ctx={ctx} korpus={pop.korpus} floors={struct[pop.korpus] || []} at={pop.at}
        onClose={() => setPop(null)} onDone={m => { setMsg(m); setPop(null); changed(true); }} onFail={fail} />}
      {pop?.kind === 'dopFloor' && <DopFloorPop ctx={ctx} korpus={pop.korpus} floors={struct[pop.korpus] || []} at={pop.at}
        onClose={() => setPop(null)} onDone={m => { setMsg(m); setPop(null); changed(); }} onFail={fail} />}
      {pop?.kind === 'newBuilding' && <NewBuildingPop ctx={ctx} at={pop.at}
        onClose={() => setPop(null)} onDone={m => { setMsg(m); setPop(null); changed(true); }} onFail={fail} />}
      {pop?.kind === 'bulk' && <BulkPop ids={[...sel]} at={pop.at} onClose={() => setPop(null)}
        onDone={m => { setMsg(m); setPop(null); setSel(new Set()); setSelLast(null); changed(); }} onFail={fail} />}
      {pop?.kind === 'prioGruppy' && <PrioGruppyPop gruppa={gruppa} kolvo={sel.size} at={pop.at}
        onClose={() => setPop(null)}
        onApply={async v => { await setPriorGruppy([...sel], v); setPop(null); setSel(new Set()); setSelLast(null); }} />}
    </div>
  );
}

/** Приоритет прямо в плитке: значение всегда из данных, при ошибке правка откатывается */
function PriInput({ f, can, gruppa, onSave }: {
  f: Floor; can: boolean; gruppa: string; onSave: (v: string) => Promise<void>;
}) {
  // выбрана группа работ — правим её приоритет (#66); пусто у группы = идёт по общему
  const znach = gruppa ? (f.prio_gruppy || {})[gruppa] ?? null : f.prioritet ?? null;
  const cur = znach != null ? String(znach) : '';
  const [v, setV] = useState(cur);
  useEffect(() => { setV(cur); }, [cur]);
  const podskazka = gruppa
    ? 'приоритет этажа для группы «' + gruppa + '»: сильнее общего; пусто — этаж идёт по общему приоритету'
      + (f.prioritet != null ? ' (сейчас ' + f.prioritet + ')' : '')
    : 'приоритет этажа: больше — раньше по всем работам, отрицательный — в конец очереди';
  return (
    <input className="sb-pri" title={podskazka}
      value={v} disabled={!can} onChange={e => setV(e.target.value)}
      style={znach != null ? { color: znach < 0 ? '#d92d20' : '#067647', fontWeight: 600 } : undefined}
      onBlur={() => { if (cur !== v.trim()) void onSave(v).then(() => setV(cur)); }}
      onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
  );
}

/** Тепловой контур прямо в ячейке (режим «пуск тепла»; модель владельца 2026-09-12):
    два события — закрытие ТК и пуск тепла, у каждого одна дата и отметка
    «случилось»: дата без отметки — план, с отметкой — факт (поле зелёное).
    Расчёт держит дату пуска тепла, плановую или свершившуюся; закрытие ТК —
    только отчётность. Отметка при пустой дате ставит сегодняшнюю (сервер). */
function PuskPolya({ f, can, onSave, onFail }: {
  f: Floor; can: boolean; onSave: (patch: Record<string, unknown>) => Promise<void>; onFail: (m: string) => void;
}) {
  const dK = fmt(f.kontur_data) || '', dP = fmt(f.pusk) || '';
  const [k, setK] = useState(dK);
  const [pp, setPp] = useState(dP);
  useEffect(() => { setK(dK); }, [dK]);
  useEffect(() => { setPp(dP); }, [dP]);

  function save(pole: string, bylo: string, stalo: string, vernut: (v: string) => void) {
    const t = stalo.trim();
    if (t === bylo) return;
    const iso = t === '' ? null : parseDate(t);
    if (iso === undefined) { onFail('Дата не разобрана: нужно ДД.ММ.ГГГГ'); vernut(bylo); return; }
    void onSave({ [pole]: iso });
  }
  const dt = (v: string, setV: (x: string) => void, bylo: string, pole: string, podskazka: string, fakt: boolean) => (
    <input className={'sb-dt' + (fakt ? ' fakt' : '')} value={v} disabled={!can}
      title={podskazka + (fakt ? ' — случилось' : ' — план') + ' (ДД.ММ.ГГГГ)'}
      onChange={e => setV(e.target.value)}
      onBlur={() => save(pole, bylo, v, setV)}
      onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
  );
  const chk = (checked: boolean, pole: string, podskazka: string) => (
    <input type="checkbox" className="sb-chk" checked={checked} disabled={!can} title={podskazka}
      onChange={e => void onSave({ [pole]: e.target.checked })} />
  );
  return (
    <span className="sb-pusk">
      <span className="sb-dr">
        <span className="sb-dl" title="Закрытие теплового контура: дата без отметки — план, с отметкой — факт. Только для отчётности">контур</span>
        {dt(k, setK, dK, 'kontur_data', 'Дата закрытия теплового контура', !!f.kontur_zakryt)}
        {chk(!!f.kontur_zakryt, 'kontur_zakryt', 'Контур закрыт — случилось. При пустой дате поставится сегодняшняя')}
      </span>
      <span className="sb-dr">
        <span className="sb-dl" title="Пуск тепла: дата без отметки — план, с отметкой — факт. Работы теплового контура не начнутся раньше этой даты">тепло</span>
        {dt(pp, setPp, dP, 'pusk_tepla', 'Дата пуска тепла — её держит расчёт работ ТК', !!f.pusk_bylo)}
        {chk(!!f.pusk_bylo, 'pusk_bylo', 'Тепло пущено — случилось. При пустой дате поставится сегодняшняя')}
      </span>
    </span>
  );
}

const Grid = ({ children }: { children: React.ReactNode }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 10px', margin: '10px 0', alignItems: 'center' }}>{children}</div>
);
const Sel = ({ value, opts, onChange, empty = '' }: { value: string; opts: string[]; onChange: (v: string) => void; empty?: string }) => (
  <select value={value} onChange={e => onChange(e.target.value)}>
    <option value="">{empty}</option>
    {opts.filter(Boolean).map(o => <option key={o} value={o}>{o}</option>)}
  </select>
);

type PopProps = { ctx: ShellCtx; onClose: () => void; onDone: (msg: string) => void; onFail: (m: string) => void };

function FloorPop({ ctx, pop, floors, onClose, onDone, onFail }: PopProps & { pop: Extract<PopState, { kind: 'floor' }>; floors: Floor[] }) {
  const f = pop.f;
  // положение среди соседей (2026-09-12): «над этажом N» → дробный номер
  const sosedi = poNomeru(floors).filter(x => x.id !== f.id);
  const [nad, setNad] = useState('');
  const novyjNum = nad === '' ? undefined : polozhenie(sosedi, nad);
  const [v, setV] = useState({ tip: f.tip || '', zahv: f.zahvatka || '', prior: f.prioritet != null ? String(f.prioritet) : '',
    pusk: fmt(f.pusk), kont: fmt(f.kontur_data), komm: f.kommentarij || '' });
  const [kz, setKz] = useState(!!f.kontur_zakryt);
  const [pb, setPb] = useState(!!f.pusk_bylo);
  const set = (k: keyof typeof v) => (val: string) => setV(s => ({ ...s, [k]: val }));
  async function save() {
    const puskIso = v.pusk.trim() === '' ? null : parseDate(v.pusk);
    if (puskIso === undefined) { onFail('Дата пуска тепла не разобрана: нужно ДД.ММ.ГГГГ'); return; }
    const kontIso = v.kont.trim() === '' ? null : parseDate(v.kont);
    if (kontIso === undefined) { onFail('Дата закрытия контура не разобрана: нужно ДД.ММ.ГГГГ'); return; }
    const body: Record<string, unknown> = {
      tip_etazha_gpr: v.tip || null, zahvatka: v.zahv.trim() || null, avto_prioritet: v.prior.trim() || null,
      pusk_tepla: puskIso, pusk_bylo: pb, kontur_data: kontIso, kontur_zakryt: kz,
      kommentarij: v.komm.trim() || null };
    if (novyjNum != null) body.floor_num = novyjNum;
    const r = await call<{ vne_zadach: number; vernulos_zadach: number }>('patch', p('/api/floors/{fid}', { fid: f.id }), body);
    if (!r.ok) { onFail(r.error.message); return; }
    onDone(r.data.vne_zadach ? 'Этаж вне расчёта: исключено задач ' + r.data.vne_zadach + '. Нажмите «Актуализировать».'
      : r.data.vernulos_zadach ? 'Этаж вернулся в расчёт: задач ' + r.data.vernulos_zadach + '. Нажмите «Актуализировать».' : '');
  }
  async function del() {
    if (!confirm('Удалить этаж «' + f.etazh + '» корпуса ' + pop.korpus + '?')) return;
    const r = await call('delete', p('/api/floors/{fid}', { fid: f.id }));
    if (!r.ok) { onFail(r.error.message); return; }
    onDone('Этаж удалён.');
  }
  void ctx;
  return (
    <Pop at={pop.at} title={pop.korpus + ' · этаж ' + f.etazh + (f.kvartir ? ' · квартир: ' + f.kvartir : '')} onClose={onClose} width={370}>
      <Grid>
        <span title="«Исключение» — этаж вне расчёта: все его задачи уходят из модели, вернуть — сменой типа обратно">Тип этажа</span>
        <Sel value={v.tip} opts={TIPY_ETAZHA} onChange={set('tip')} />
        {/* «Положение» — только у этажей, заведённых вручную (#29): у обычных номер = метка */}
        {ruchnoyEtazh(f) && (<>
          <span title="Номер для порядка: по нему этаж стоит среди соседей в структуре, шахматке и очереди «Снизу вверх»">Положение</span>
          <select value={nad} onChange={e => setNad(e.target.value)}>
            <option value="">{f.num != null ? 'номер ' + f.num + ' — не менять' : 'номера нет — задать'}</option>
            {sosedi.map(x => <option key={x.id} value={String(x.id)}>над этажом {x.etazh}</option>)}
            {sosedi.length > 0 && <option value="low">под самым нижним</option>}
          </select>
          {novyjNum != null && <><span /><span className="pw-muted">станет номер {novyjNum}</span></>}
        </>)}
        <span>Приоритет</span><input value={v.prior} placeholder="больше — раньше, минус — в конец" onChange={e => set('prior')(e.target.value)} />
        <span title="Закрытие теплового контура — только для отчётности. Дата без отметки — план, с отметкой — факт">Закрытие ТК</span>
        <span className="sb-sob">
          <input value={v.kont} placeholder="ДД.ММ.ГГГГ" onChange={e => set('kont')(e.target.value)} />
          <label title="Случилось. При пустой дате поставится сегодняшняя"><input type="checkbox" checked={kz} onChange={e => setKz(e.target.checked)} /> готово</label>
        </span>
        <span title="Работы теплового контура не начнутся раньше этой даты. Дата без отметки — план, с отметкой — факт">Пуск тепла</span>
        <span className="sb-sob">
          <input value={v.pusk} placeholder="ДД.ММ.ГГГГ" onChange={e => set('pusk')(e.target.value)} />
          <label title="Случилось. При пустой дате поставится сегодняшняя"><input type="checkbox" checked={pb} onChange={e => setPb(e.target.checked)} /> готово</label>
        </span>
        <span>Комментарий</span><input value={v.komm} onChange={e => set('komm')(e.target.value)} />
      </Grid>
      <div className="bar" style={{ margin: 0 }}>
        <button onClick={() => void save()}>Сохранить</button>
        <button className="del" onClick={() => void del()}>Удалить этаж</button>
      </div>
    </Pop>
  );
}

function BuildingPop({ ctx, korpus, floors, at: a, onClose, onDone, onFail }: PopProps & { korpus: string; floors: Floor[]; at: Anchor }) {
  const nums = floors.filter(f => f.num != null && f.num === Math.round(f.num) && String(Math.round(f.num)) === String(f.etazh))
    .map(f => Math.round(f.num as number)).filter(n => n > 0);
  const curN = nums.length ? Math.max(...nums) : 0;
  const [name, setName] = useState(korpus);
  const [cnt, setCnt] = useState('');
  const [pod, setPod] = useState('');
  // секции корпуса (#70, 2026-09-18): «А, Б» — этаж делится на секции, и работы
  // с раскаткой «по этажам и секциям» получают задачу на каждую
  const [sek, setSek] = useState('');
  const [sekBylo, setSekBylo] = useState('');
  useEffect(() => {
    void (async () => {
      const r = await call<Record<string, string[]>>('get', p('/api/projects/{pid}/sekcii', { pid: ctx.pid }));
      const t = r.ok ? (r.data[korpus] || []).join(', ') : '';
      setSek(t); setSekBylo(t);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [korpus, ctx.pid]);
  const podEst = floors.filter(f => f.num != null && (f.num as number) < 0).length;
  async function save() {
    const msgs: string[] = [];
    // подземные этажи только досоздаются (удалять их через этажность нельзя —
    // «Этажность» считает наземные 1…N)
    const np = podzemnyh(pod);
    if (np === null) { onFail('Подземных этажей — целое число от 1 до 300.'); return; }
    if (np) {
      const rp = await call<{ sozdano: number; uzhe_byli: number }>('post',
        p('/api/projects/{pid}/floors/bulk', { pid: ctx.pid }),
        { korpus, ot: -np, do: -1, tip_etazha: TIP_PODZEM });
      if (!rp.ok) { onFail(rp.error.message); return; }
      if (rp.data.sozdano) msgs.push('добавлено подземных: ' + rp.data.sozdano);
    }
    if (sek.trim() !== sekBylo.trim()) {
      const spisok = sek.split(',').map(x => x.trim()).filter(Boolean);
      const rs = await call<{ sozdano_zadach: number; udaleno_zadach: number }>('put',
        p('/api/projects/{pid}/sekcii', { pid: ctx.pid }), { korpus, sekcii: spisok });
      if (!rs.ok) { onFail(rs.error.message); return; }
      msgs.push('секции: ' + (spisok.join(', ') || 'убраны')
        + (rs.data.sozdano_zadach ? ', создано задач ' + rs.data.sozdano_zadach : '')
        + (rs.data.udaleno_zadach ? ', убрано задач ' + rs.data.udaleno_zadach : ''));
    }
    if (cnt.trim() !== '') {
      const n = parseInt(cnt, 10);
      if (!Number.isInteger(n) || n < 1 || n > 300) { onFail('Этажность — целое число от 1 до 300.'); return; }
      if (n !== curN) {
        const r = await call<{ sozdano: number; udaleno: number }>('post', p('/api/projects/{pid}/buildings/resize', { pid: ctx.pid }),
          { korpus, etazhnost: n });
        if (!r.ok) { onFail(r.error.message); return; }
        if (r.data.sozdano) msgs.push('добавлено этажей: ' + r.data.sozdano);
        if (r.data.udaleno) msgs.push('удалено этажей сверху: ' + r.data.udaleno);
      }
    }
    const nn = name.trim();
    if (nn && nn !== korpus) {
      const r = await call('patch', p('/api/projects/{pid}/buildings', { pid: ctx.pid }), { name: korpus, new_name: nn });
      if (!r.ok) { onFail(r.error.message); return; }
      msgs.push('переименован в «' + nn + '»');
    }
    onDone(msgs.length ? 'Корпус ' + korpus + ': ' + msgs.join(', ') + '.' : 'Изменений нет.');
  }
  async function del() {
    if (!confirm('Удалить корпус «' + korpus + '» со всеми его этажами? Если в корпусе есть задачи, удаление не пройдёт.')) return;
    const r = await call<{ etazhej: number }>('delete', p('/api/projects/{pid}/buildings', { pid: ctx.pid }) + '?name=' + encodeURIComponent(korpus));
    if (!r.ok) { onFail(r.error.message); return; }
    onDone('Корпус «' + korpus + '» удалён' + (r.data.etazhej ? ' вместе с ' + r.data.etazhej + ' этажами' : '') + '.');
  }
  return (
    <Pop at={a} title={'Корпус ' + korpus} onClose={onClose} width={370}>
      <Grid>
        <span>Название</span><input value={name} onChange={e => setName(e.target.value)} />
        <span>Этажность</span><input value={cnt} placeholder={'сейчас: ' + curN} onChange={e => setCnt(e.target.value)} />
        <span title="Этажи −1, −2, −3 — паркинг и подвалы">Подземных этажей</span>
        <input value={pod} placeholder={'сейчас: ' + podEst} onChange={e => setPod(e.target.value)} />
        <span title="Секции внутри этажа, через запятую (например «А, Б»). Задачи по секциям получают только работы с раскаткой «по этажам и секциям»">Секции</span>
        <input value={sek} placeholder="например: А, Б" onChange={e => setSek(e.target.value)} />
      </Grid>
      <div className="pw-muted" style={{ margin: '0 0 8px' }}>Секции делят этаж: у работ с раскаткой «по этажам и секциям»
        появится задача на каждую секцию, остальные работы останутся одной задачей на этаж. Убрать секцию можно,
        пока по её задачам нет факта и ручных правок.</div>
      <div className="pw-muted" style={{ margin: '0 0 8px' }}>Этажность — число номерных этажей 1…N: недостающие досоздадутся,
        лишние сверху удалятся (если на них нет задач). Подземные (−1, −2, −3) только досоздаются, с типом «{TIP_PODZEM}»;
        лишний подземный этаж удаляется кликом по нему. Особые этажи (Тех.этаж, кровля) не трогаются.</div>
      <div className="bar" style={{ margin: 0 }}>
        <button onClick={() => void save()}>Сохранить</button>
        <button className="del" onClick={() => void del()}>Удалить корпус</button>
      </div>
    </Pop>
  );
}

/** этажи корпуса с номером, сверху вниз */
function poNomeru(floors: Floor[]): Floor[] {
  return floors.filter(f => f.num != null).sort((x, y) => (y.num as number) - (x.num as number));
}

/** Положение «над этажом» → номер (2026-09-12): середина между выбранным этажом и
    следующим сверху; над самым верхним — +0,5 (кровля над 48-м = 48,5);
    «под самым нижним» — −0,5. `sosedi` — этажи корпуса с номером, сверху вниз. */
function polozhenie(sosedi: Floor[], nad: string): number | null {
  if (!sosedi.length) return null;
  if (nad === 'low') return Math.round(((sosedi[sosedi.length - 1].num as number) - 0.5) * 1000) / 1000;
  const i = sosedi.findIndex(x => String(x.id) === nad);
  if (i < 0) return null;
  const n = sosedi[i].num as number;
  const verh = i > 0 ? (sosedi[i - 1].num as number) : null;
  return Math.round((verh == null ? n + 0.5 : (n + verh) / 2) * 1000) / 1000;
}

/** Доп. этаж (решение владельца 2026-09-12): кровля, технический этаж между жилыми
    и т.п. Метка свободная («1т», «К»), тип из системного набора, а место задаётся
    словами — «над этажом 1» — и превращается в дробный номер (1,5): по нему этаж
    встаёт между соседями в структуре, шахматке и очереди «Снизу вверх». Поэтажные
    типовые работы на такие этажи не ложатся; свой набор работ — раскаткой
    «на технические этажи» (шаг 4) или ручным вводом. */
function DopFloorPop({ ctx, korpus, floors, at: a, onClose, onDone, onFail }: PopProps & { korpus: string; floors: Floor[]; at: Anchor }) {
  const sosedi = poNomeru(floors);
  const [v, setV] = useState({ label: '', tip: 'Технический', nad: sosedi[0] ? String(sosedi[0].id) : 'low', zahv: '' });
  const set = (k: keyof typeof v) => (val: string) => setV(s => ({ ...s, [k]: val }));
  const num = polozhenie(sosedi, v.nad);
  async function add() {
    const label = v.label.trim();
    if (!label) { onFail('Введите метку этажа — например «1т» или «К».'); return; }
    if (num == null) { onFail('В корпусе нет этажей с номерами — сначала заведите этажи.'); return; }
    const r = await call<{ sozdano: number; uzhe_byli: number }>('post', p('/api/projects/{pid}/floors/bulk', { pid: ctx.pid }),
      { korpus, label, num, tip_etazha: v.tip, zahvatka: v.zahv.trim() });
    if (!r.ok) { onFail(r.error.message); return; }
    onDone(r.data.sozdano ? 'Доп. этаж «' + label + '» добавлен, номер ' + num + '.' : 'Этаж «' + label + '» уже есть.');
  }
  return (
    <Pop at={a} title={'Доп. этаж в корпус ' + korpus} onClose={onClose} width={380}>
      <div className="pw-muted" style={{ margin: '4px 0 8px' }}>Кровля, технический этаж между жилыми и т.п.
        Поэтажные типовые работы на него не ложатся; на технические этажи ложатся работы с раскаткой
        «по техническим этажам». Место задаётся словами, номер для порядка считается сам.</div>
      <Grid>
        <span>Метка</span><input autoFocus placeholder="напр. 1т или К" value={v.label} onChange={e => set('label')(e.target.value)} />
        <span>Тип</span>
        <select value={v.tip} onChange={e => set('tip')(e.target.value)}>
          {TIPY_ETAZHA.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <span>Положение</span>
        <select value={v.nad} onChange={e => set('nad')(e.target.value)}>
          {sosedi.map(x => <option key={x.id} value={String(x.id)}>над этажом {x.etazh}</option>)}
          <option value="low">под самым нижним</option>
        </select>
        <span /><span className="pw-muted">номер для порядка: {num ?? '—'}</span>
      </Grid>
      <div className="bar" style={{ margin: 0 }}><button onClick={() => void add()}>Добавить</button></div>
    </Pop>
  );
}

function NewBuildingPop({ ctx, at: a, onClose, onDone, onFail }: PopProps & { at: Anchor }) {
  const [name, setName] = useState('');
  const [floors, setFloors] = useState('');
  const [pod, setPod] = useState('');
  async function create() {
    const nm = name.trim();
    if (!nm) { onFail('Введите название корпуса.'); return; }
    // «Этажность» с минусом — это подземные этажи (так его и вводят для паркинга)
    const naz = floors.trim();
    const minus = naz.startsWith('-');
    const n = naz === '' || minus ? 0 : parseInt(naz, 10);
    if (naz !== '' && !minus && (!Number.isInteger(n) || n < 1 || n > 300)) {
      onFail('Этажность — целое число от 1 до 300 (подземные этажи — в поле «Подземных этажей»).'); return;
    }
    const np = podzemnyh(minus ? naz : pod);
    if (np === null) { onFail('Подземных этажей — целое число от 1 до 300.'); return; }
    const r = await call('post', p('/api/projects/{pid}/buildings', { pid: ctx.pid }), { name: nm });
    if (!r.ok) { onFail(r.error.message); return; }
    let t = 'Корпус «' + nm + '» создан';
    if (n) {
      const rf = await call<{ sozdano: number }>('post', p('/api/projects/{pid}/floors/bulk', { pid: ctx.pid }), { korpus: nm, ot: 1, do: n });
      t += rf.ok ? ', этажей: ' + rf.data.sozdano : ', но этажи не создались: ' + rf.error.message;
    }
    if (np) {
      const rp = await call<{ sozdano: number }>('post', p('/api/projects/{pid}/floors/bulk', { pid: ctx.pid }),
        { korpus: nm, ot: -np, do: -1, tip_etazha: TIP_PODZEM });
      t += rp.ok ? ', подземных: ' + rp.data.sozdano : ', но подземные не создались: ' + rp.error.message;
    }
    if (!n && !np) t += ' — добавьте этажи плюсиком под ним';
    onDone(t + '.');
  }
  return (
    <Pop at={a} title="Новый корпус" onClose={onClose} width={360}>
      <Grid>
        <span>Название</span><input autoFocus placeholder="напр. К1 или Паркинг" value={name} onChange={e => setName(e.target.value)} />
        <span>Этажность</span><input placeholder="напр. 25" value={floors} onChange={e => setFloors(e.target.value)} />
        <span title="Этажи −1, −2, −3 — паркинг и подвалы">Подземных этажей</span>
        <input placeholder="напр. 3" value={pod} onChange={e => setPod(e.target.value)} />
      </Grid>
      <div className="pw-muted" style={{ margin: '0 0 8px' }}>Этажи 1…N создадутся сразу (1-й — тип «Первый», остальные — «Типовой»),
        подземные — с типом «{TIP_PODZEM}»: поэтажные работы на них не ложатся. Оба поля можно оставить пустыми,
        особые этажи (Тех.этаж, кровля) добавите потом кнопкой «+ доп. этаж» под колонкой.</div>
      <div className="bar" style={{ margin: 0 }}><button onClick={() => void create()}>Создать</button></div>
    </Pop>
  );
}

/** Приоритет выбранной группы работ сразу нескольким этажам (#66, 2026-09-18):
    мультивыбор на доске + одно значение; пустое поле снимает исключение. */
function PrioGruppyPop({ gruppa, kolvo, at: a, onClose, onApply }: {
  gruppa: string; kolvo: number; at: Anchor; onClose: () => void; onApply: (v: string) => Promise<void>;
}) {
  const [v, setV] = useState('');
  return (
    <Pop at={a} onClose={onClose} title={'Приоритет группы «' + gruppa + '»'}>
      <Grid>
        <span>Приоритет</span>
        <input autoFocus value={v} placeholder="больше — раньше, минус — в конец, пусто — снять"
          onChange={e => setV(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') void onApply(v); }} />
      </Grid>
      <div className="pw-muted" style={{ margin: '0 0 8px' }}>
        Задастся выбранным этажам ({kolvo}) только для работ группы «{gruppa}» — он сильнее общего
        приоритета этажа. Пустое поле снимает исключение: этажи снова идут по общему приоритету.
        Даты обновятся после «Актуализировать».</div>
      <div className="bar" style={{ margin: 0 }}><button onClick={() => void onApply(v)}>Задать</button></div>
    </Pop>
  );
}

function BulkPop({ ids, at: a, onClose, onDone, onFail }: { ids: number[]; at: Anchor } & Omit<PopProps, 'ctx'>) {
  const [v, setV] = useState({ tip: '', zahv: '', prior: '', pusk: '', kont: '', kz: '', pb: '' });
  const set = (k: keyof typeof v) => (val: string) => setV(s => ({ ...s, [k]: val }));
  const selVal = (x: string) => (x === '' ? undefined : x === '__clear' ? null : x);
  const txtVal = (x: string) => { const t = x.trim(); return t === '' ? undefined : t === '-' ? null : t; };
  async function apply() {
    const body: Record<string, unknown> = { ids };
    const tip = selVal(v.tip); if (tip !== undefined) body.tip_etazha_gpr = tip;
    if (v.kz !== '') body.kontur_zakryt = v.kz === 'da';
    if (v.pb !== '') body.pusk_bylo = v.pb === 'da';
    const zahv = txtVal(v.zahv); if (zahv !== undefined) body.zahvatka = zahv;
    const prior = txtVal(v.prior); if (prior !== undefined) body.avto_prioritet = prior;
    const pusk = txtVal(v.pusk);
    if (pusk !== undefined) {
      const iso = pusk === null ? null : parseDate(pusk);
      if (iso === undefined) { onFail('Дата пуска тепла не разобрана: нужно ДД.ММ.ГГГГ'); return; }
      body.pusk_tepla = iso;
    }
    const kont = txtVal(v.kont);
    if (kont !== undefined) {
      const iso = kont === null ? null : parseDate(kont);
      if (iso === undefined) { onFail('Дата закрытия контура не разобрана: нужно ДД.ММ.ГГГГ'); return; }
      body.kontur_data = iso;
    }
    if (Object.keys(body).length === 1) { onFail('Заполните хотя бы одно поле.'); return; }
    const r = await call<{ obnovleno: number; vne_zadach: number; vernulos_zadach: number }>('post', '/api/floors/bulk-patch', body);
    if (!r.ok) { onFail(r.error.message); return; }
    onDone('Обновлено этажей: ' + r.data.obnovleno + '.'
      + (r.data.vne_zadach ? ' Исключено задач: ' + r.data.vne_zadach + '.' : '')
      + (r.data.vernulos_zadach ? ' Вернулось задач: ' + r.data.vernulos_zadach + '.' : '')
      + (r.data.vne_zadach || r.data.vernulos_zadach ? ' Нажмите «Актуализировать».' : ''));
  }
  const BulkSel = ({ value, list, onChange }: { value: string; list: string[]; onChange: (v: string) => void }) => (
    <select value={value} onChange={e => onChange(e.target.value)}>
      <option value="">— не менять —</option><option value="__clear">— очистить —</option>
      {list.filter(Boolean).map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  return (
    <Pop at={a} title={'Свойства ' + ids.length + ' выбранных этажей'} onClose={onClose}>
      <div className="pw-muted" style={{ margin: '4px 0 8px' }}>Заполненные поля применятся ко всем выбранным этажам; пустые не трогаются.
        Очистить текстовое поле — поставьте «-».</div>
      <Grid>
        <span>Тип этажа</span><BulkSel value={v.tip} list={TIPY_ETAZHA} onChange={set('tip')} />
        <span>Приоритет</span><input value={v.prior} onChange={e => set('prior')(e.target.value)} />
        <span title="Только для отчётности">Закрытие ТК, дата</span>
        <input value={v.kont} placeholder="ДД.ММ.ГГГГ" onChange={e => set('kont')(e.target.value)} />
        <span title="Отметка «случилось»: при пустой дате поставится сегодняшняя">Закрытие ТК</span>
        <select value={v.kz} onChange={e => set('kz')(e.target.value)}>
          <option value="">— не менять —</option>
          <option value="da">готово</option>
          <option value="net">не готово (план)</option>
        </select>
        <span title="Работы теплового контура не начнутся раньше этой даты">Пуск тепла, дата</span>
        <input value={v.pusk} placeholder="ДД.ММ.ГГГГ" onChange={e => set('pusk')(e.target.value)} />
        <span title="Отметка «случилось»: при пустой дате поставится сегодняшняя">Пуск тепла</span>
        <select value={v.pb} onChange={e => set('pb')(e.target.value)}>
          <option value="">— не менять —</option>
          <option value="da">готово</option>
          <option value="net">не готово (план)</option>
        </select>
      </Grid>
      <div className="bar" style={{ margin: 0 }}><button onClick={() => void apply()}>Применить</button></div>
    </Pop>
  );
}
